import { Loader } from '@bitstopco/bitstop-theme';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Card,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import ReconciliationCard from '@/components/restocking/ReconciliationCard';

import {
  useCreateReconciliationMutation,
  useGetRestockRequestQuery,
} from '@/store/api/services/restocking/restocking';

import { DEFAULT_ERROR_MESSAGE } from '@/constants/app/config';
import { RESIDUAL_BAG_LABELS } from '@/constants/app/restocking';

import type { ReconciliationRequest } from '@/types/generated/boro';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';

const CreateReconciliation = ({ props, hide }: ModalChildrenProps) => {
  const id = props?.id;
  const [request, setRequest] = useState<Record<string, Record<string, number | null>>>({});
  const [create, { isLoading }] = useCreateReconciliationMutation();
  const [note, setNote] = useState<string>('');

  const {
    data: { data: order = null } = {},
    isLoading: isLoadingOrder,
    error,
  } = useGetRestockRequestQuery(id ?? skipToken);

  const isValid = useMemo(() => {
    const allValues = Object.values(request).flatMap(Object.values);
    const billsCount = Object.values(order?.residual_bags ?? {}).flatMap((bag: any) =>
      Object.values(bag?.contents ?? {}),
    ).length;
    return allValues.length === billsCount && allValues.every((v) => v !== null);
  }, [request, order]);

  const handleUpdate = async () => {
    try {
      await create({
        id,
        data: {
          note,
          confirmed_note_counts: request as ReconciliationRequest['confirmed_note_counts'],
        },
      }).unwrap();
      hide();
      toast.success('Reconciliation created successfully');
    } catch (error) {
      toast.error((error as any).message ?? DEFAULT_ERROR_MESSAGE);
    }
  };

  useEffect(() => {
    if (error) hide();
  }, [error]);

  const handleChange = (bagId: string, noteId: string, value: number | null) =>
    setRequest((prev) => ({
      ...prev,
      [bagId]: {
        ...prev[bagId],
        [noteId]: value,
      },
    }));

  if (isLoadingOrder) return <Loader />;
  return (
    <>
      <DialogContent>
        <Stack gap={3} py={3}>
          {order?.residual_bags?.map((bag) => {
            return (
              <Card key={bag.id}>
                <Stack p={2} gap={2}>
                  <Typography variant="h6">
                    {RESIDUAL_BAG_LABELS[bag.type!]}{' '}
                    {bag.residual_bag_id ? `(${bag.residual_bag_id})` : ''}
                  </Typography>
                  {bag.contents?.map((content, idx) => (
                    <ReconciliationCard
                      onChange={(...props) => handleChange(bag.id!, ...props)}
                      content={content}
                      key={idx}
                    />
                  ))}
                </Stack>
              </Card>
            );
          })}
          <Stack gap={1}>
            <Typography variant="h6">Note</Typography>
            <TextField
              value={note}
              onChange={({ target }) => setNote(target.value)}
              placeholder="Note"
              variant="standard"
              fullWidth
              multiline
            />
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={hide}>Close</Button>
        <LoadingButton
          disabled={!isValid}
          loading={isLoading}
          onClick={handleUpdate}
          variant="contained"
        >
          Create Reconciliation
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default CreateReconciliation;
